import { FoodsIllustrations, OptionsPopoverOption, ScoreCard, TrajectoryChart, WhiteBox } from "@foodpilot/foods";
import { Stack, Typography, useTheme } from "@mui/material";
import { TrajectoryBarData } from "node_modules/@foodpilot/foods/dist/components/Chart/TrajectoryChart/formatChart";
import { useTranslation } from "react-i18next";
import { ScoreValue } from "src/api/client-api/post/scoreSchema";
import { APMetadata } from "src/context/Actions/useActionPlanData";
import { useActionsContext } from "src/context/Actions/useActionsContext";

type SynthesisBarChartProps = unknown;
export const SynthesisBarChart = (_props: SynthesisBarChartProps) => {
    const { allScoreWithVariations, selectedScore, actionPlanData, persistedOptions, actionPlanMetadata } =
        useActionsContext();
    const { t } = useTranslation();

    const data = allScoreWithVariations.flatMap((score) => {
        const foundScore = actionPlanData.find((item) => {
            return item.scoreId === score.scoreId && item.variationId === score.variationId;
        });
        if (foundScore === undefined) return [];

        return [
            {
                ...score,
                onClick: (option: OptionsPopoverOption) => {
                    score.onClick();
                },
            },
        ];
    });

    const actionPlanScores = actionPlanData.find((actionPlan) => {
        const doesScoreMatch = actionPlan.scoreId === selectedScore?.scoreId;
        const doesVariationMatch = actionPlan.variationId === selectedScore?.variationId;
        return doesScoreMatch && doesVariationMatch;
    });
    if (actionPlanScores === undefined) {
        return <NoGoalDefined />;
    }

    const startingPost = actionPlanScores.metadata.startingPost;
    const startingData = [
        {
            label: startingPost.scoreByLabel,
            value: startingPost.score as number,
        },
    ];

    const trajectoryData = formatTrajectoryData(actionPlanScores.metadata, actionPlanMetadata.isGrowthDisabled);
    const trajectoryGoal = actionPlanScores.metadata.goal?.value;

    return (
        <WhiteBox>
            <Stack>
                <ScoreCard
                    header={{
                        selectedItemId: selectedScore !== undefined ? selectedScore.rowId : 1,
                        options: data.map((d) => ({ ...d, id: d.rowId })),
                        variant: "secondary",
                        orientation: "horizontal",
                    }}
                    disableFooter={true}
                >
                    <TrajectoryChart
                        goal={trajectoryGoal}
                        unit={selectedScore?.unit}
                        height="600px"
                        initialValues={startingData}
                        improvement={selectedScore?.improvement}
                        trajectory={trajectoryData}
                        trajectoryOptions={{
                            initialValues: { label: t("trajectory-chart.calculatedScore") },
                            target: { label: t("trajectory-chart.goal"), color: "#3A86FF" },
                            main: { label: persistedOptions.withoutPlanName },
                            difference: { label: persistedOptions.withPlanName },
                        }}
                        textOptions={{
                            complete: t("trajectory-chart.complete"),
                            between: t("trajectory-chart.between"),
                            and: t("trajectory-chart.and"),
                            withoutActionPlan: persistedOptions.comparedToWithoutPlan,
                            goal: t("trajectory-chart.goal"),
                            noObjective: t("trajectory-chart.noObjective"),

                            // Whenever we implement the opening of a modal with a link
                            // We can use this parameter
                            // editTrajectories: t("trajectory-chart.editTrajectories"),
                        }}
                    />
                </ScoreCard>
            </Stack>
        </WhiteBox>
    );
};

const formatTrajectoryData = (actionPlan: APMetadata, isGrowthDisabled: boolean): TrajectoryBarData[] => {
    const acc: Record<string, [ScoreValue, ScoreValue]> = {};

    const _trajectoryData = actionPlan.scoresWithActionPost.details.reduce((acc, value) => {
        if (Object.keys(acc).includes(value.scoreByLabel) === false) {
            acc[value.scoreByLabel] = [null, null];
        }
        acc[value.scoreByLabel][1] = value.score;
        return acc;
    }, acc);

    const trajectoryData = actionPlan.scoresWithoutActionPost.details.reduce((acc, value) => {
        if (Object.keys(acc).includes(value.scoreByLabel) === false) {
            acc[value.scoreByLabel] = [null, null];
        }

        const isGrowthEnabled = isGrowthDisabled === false;
        if (isGrowthEnabled) {
            acc[value.scoreByLabel][0] = value.score;
        }
        return acc;
    }, _trajectoryData);

    const formattedTrajectory = Object.entries(trajectoryData).map(([k, v]) => {
        return {
            label: k,
            values: v,
        };
    });
    return formattedTrajectory;
};

const NoGoalDefined = () => {
    const theme = useTheme();
    const { t } = useTranslation();

    return (
        <WhiteBox>
            <Stack gap={theme.spacing(4)} padding={theme.spacing(8, 4)} justifyContent={"center"} alignItems={"center"}>
                <FoodsIllustrations illustration={"notEnoughData"} />
                <Typography variant="body-bold">{t("actionPlan.trajectory.notEnoughGoals")}</Typography>
            </Stack>
        </WhiteBox>
    );
};
